import { Check, CircleAlert } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@components/ui/button';
import { cn } from '@core/utils/cn';
import { FormSection } from '../../../components/form-section';
import { Input } from '../../../components/Input';
import { Option, Select } from '../../../components/select';
import { StickyBar } from '../../../components/sticky-bar';
import { FieldLabel } from '../../../components/textfield';
import {
    CountryOptionDto,
    CustomFacilityDetailsDto,
    SelectEntryDto
} from '../../../core/api/StudioClient';
import { FormField } from '../../../core/forms';
import { InitialData } from '../../../core/initial-data';
import { links } from '../../../core/router';
import { injectTSDI } from '../../../core/tsdi';
import { AdditionalData } from '../additional-data';
import { NearbyFacilities } from '../nearby-facilities';
import { FacilityEditStore } from '../store';
import { useStore } from './store';

type FieldStatus = 'same' | 'changes' | 'empty';
type InputType = 'text' | 'number' | 'select';

const statusBackground: Record<FieldStatus, string> = {
    same: 'bg-muted',
    changes: 'bg-destructive/15',
    empty: 'bg-muted/40'
};

const comparisonRowClasses =
    'grid grid-cols-[150px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-3 border-b py-1.5 last:border-b-0';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ComparisonRowProps<T extends any = any> {
    label?: string;
    fields: {
        internal: FormField<T | undefined>;
        external: FormField<T | undefined> | null;
    };
    options?: SelectEntryDto[];
    type?: InputType;
    required?: boolean;
    disabled?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface SectionInputProps<T extends any = any> {
    type?: InputType;
    field?: FormField<T | undefined>;
    options?: SelectEntryDto[];
    disabled?: boolean;
}

interface ActionsBarProps {
    facility: CustomFacilityDetailsDto;

    onSave(): void;

    onReject(): void;
}

function getStatusIcon(status: FieldStatus) {
    switch (status) {
        case 'changes':
            return <CircleAlert className="size-4 shrink-0 text-destructive" />;
        case 'same':
            return <Check className="size-4 shrink-0 text-success" />;
        default:
            return null;
    }
}

const SectionInput = observer(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends any>({
        type,
        field,
        options = [],
        disabled
    }: SectionInputProps<T>) => {
        if (type === 'select') {
            const selectOptions: Option<string>[] = options.map((option) => ({
                label: option.label || '',
                value: option.value || ''
            }));

            return (
                <Select
                    noLabel
                    label="Select"
                    value={field?.value}
                    disabled={disabled}
                    options={selectOptions}
                    onChange={(value) => field?.onChange(value as T)}
                />
            );
        }

        return (
            <Input
                value={(field?.value as string | number | undefined) ?? ''}
                edit="true"
                type={type}
                onChange={(event) => {
                    field?.onChange(event.target.value as T);
                }}
                disabled={disabled}
            />
        );
    }
);

const ComparisonRow = observer(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends any>({
        label,
        fields,
        type,
        options,
        required,
        disabled
    }: ComparisonRowProps<T>) => {
        const sameFields =
            `${fields.external?.value}`.trimEnd() ===
            `${fields.internal?.value}`.trimEnd();

        const status: FieldStatus = !fields.external?.value
            ? 'empty'
            : sameFields
              ? 'same'
              : 'changes';

        return (
            <div className={comparisonRowClasses}>
                <FieldLabel label={label} required={required} />
                <div
                    className={cn(
                        'flex items-center gap-2 rounded-md px-2',
                        fields.internal.error && statusBackground.changes
                    )}
                >
                    <div className="min-w-0 flex-1">
                        <SectionInput
                            field={fields.internal}
                            type={type}
                            options={options}
                            disabled={disabled}
                        />
                    </div>
                    {getStatusIcon(status)}
                </div>
                <div
                    className={cn(
                        'rounded-md px-2 opacity-70',
                        statusBackground[status]
                    )}
                >
                    <Input
                        defaultValue={
                            fields.external?.value as
                                | string
                                | number
                                | undefined
                        }
                        disabled
                    />
                </div>
            </div>
        );
    }
);

const FacilityDetailsForm = observer(() => {
    const { studioCategories, availableCountries } = injectTSDI(InitialData);
    const { facility } = injectTSDI(FacilityEditStore);

    return facility.match({
        fetched: (facility) => (
            <>
                <FacilityEditForm
                    customFacility={facility}
                    studioCategories={studioCategories.$}
                    countryOptions={availableCountries.$}
                />
                <NearbyFacilities studio={facility.internalData} />
            </>
        )
    });
});

interface FacilityEditFormProps {
    customFacility: CustomFacilityDetailsDto;
    studioCategories: SelectEntryDto[];
    countryOptions: CountryOptionDto[];
}

const FacilityEditForm = observer(
    ({
        customFacility,
        studioCategories,
        countryOptions
    }: FacilityEditFormProps) => {
        const {
            fields,
            selectedExternalData,
            onSubmitHandler,
            onRejectHandler,
            selectedDataName,
            additionalData
        } = useStore(customFacility);

        return (
            <>
                <div className="flex flex-col gap-6 pb-24">
                    <div className={cn(comparisonRowClasses, 'border-b-0')}>
                        <div />
                        <h2 className="text-lg font-semibold tracking-tight">
                            User Input
                        </h2>
                        <div className="flex flex-wrap items-baseline gap-2">
                            <h2 className="text-lg font-semibold tracking-tight">
                                {selectedDataName}
                            </h2>
                            {!selectedExternalData && (
                                <span className="text-xs font-medium text-destructive italic">
                                    Source is empty
                                </span>
                            )}
                        </div>
                    </div>

                    <FormSection title="Details">
                        <ComparisonRow
                            label="Name"
                            fields={fields.name}
                            required
                        />
                        <ComparisonRow
                            label="Category"
                            fields={fields.category}
                            options={studioCategories}
                            type="select"
                            required
                        />
                    </FormSection>

                    <FormSection title="Address">
                        <ComparisonRow
                            label="City"
                            fields={fields.address.city}
                            required
                        />
                        <ComparisonRow
                            label="Street"
                            fields={fields.address.street}
                            required
                        />
                        <ComparisonRow
                            label="House No."
                            fields={fields.address.houseNumber}
                            required
                        />
                        <ComparisonRow
                            label="Zip"
                            fields={fields.address.zip}
                            required
                        />
                        <ComparisonRow
                            label="Country"
                            type="select"
                            options={countryOptions}
                            fields={fields.address.country}
                            required
                        />
                        <ComparisonRow
                            label="Latitude"
                            fields={fields.address.latitude}
                            type="number"
                            disabled
                            required
                        />
                        <ComparisonRow
                            label="Longitude"
                            fields={fields.address.longitude}
                            type="number"
                            disabled
                            required
                        />
                    </FormSection>

                    {additionalData && <AdditionalData data={additionalData} />}
                </div>
                <ActionsBar
                    facility={customFacility}
                    onSave={onSubmitHandler}
                    onReject={onRejectHandler}
                />
            </>
        );
    }
);

const ActionsBar = ({ facility, onSave, onReject }: ActionsBarProps) => (
    <StickyBar>
        <Button variant="ghost" asChild>
            <Link to={links.facility()}>Cancel</Link>
        </Button>
        {facility.internalData.status !== 'DELETED' && (
            <Button variant="destructive" onClick={onReject}>
                Reject
            </Button>
        )}
        <Button onClick={onSave}>
            {facility.internalData.status === 'VERIFIED' ? 'Save' : 'Approve'}
        </Button>
    </StickyBar>
);

export default FacilityDetailsForm;
